import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Ohne diese Grenze reisst ein einziger unerwarteter Fehler die komplette
 * Oberflaeche ab: React haengt den ganzen Baum aus, uebrig bleibt der leere
 * Hintergrund - die App sieht dann eingefroren aus, ohne jeden Hinweis worin
 * das Problem besteht. Genau so ist die Android-App an einem fehlenden
 * `crypto.randomUUID` gestorben. Lieber eine haessliche, aber ehrliche
 * Fehlermeldung als ein schwarzer Bildschirm.
 */

interface Props {
  children: ReactNode;
}

interface State {
  message: string | null;
}

const panel: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10_000,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 14,
  padding: '28px 22px',
  background: '#ffffff',
  color: '#14161c',
  font: '15px/1.5 system-ui, -apple-system, sans-serif',
  overflowY: 'auto',
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[R2F] Unerwarteter Fehler:', error, info.componentStack);
  }

  render(): ReactNode {
    const { message } = this.state;
    if (message == null) return this.props.children;

    return (
      <div style={panel} role="alert">
        <h1 style={{ margin: 0, fontSize: '1.3rem' }}>Da ist etwas schiefgelaufen</h1>
        <p style={{ margin: 0, color: '#545c6b' }}>
          Die App konnte nicht weiterlaufen. Die genaue Meldung:
        </p>
        <code
          style={{
            display: 'block',
            padding: 12,
            borderRadius: 12,
            background: '#f1f2f4',
            wordBreak: 'break-word',
            fontSize: '0.85rem',
          }}
        >
          {message}
        </code>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 4,
            padding: '13px 18px',
            borderRadius: 999,
            border: 0,
            background: '#08874c',
            color: '#fff',
            font: 'inherit',
            fontWeight: 600,
          }}
        >
          Neu laden
        </button>
      </div>
    );
  }
}
